import { cardAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

const expandableCardSlotRecipe = defineSlotRecipe({
  className: 'expandable-card',
  slots: [...cardAnatomy.keys(), 'collapsible', 'icon', 'subtitle', 'suffix'],
  base: {
    root: {
      backgroundColor: 'background/primary',
      // `clip` rather than `hidden`: a hidden box is a scroll container, which would make a
      // `position: sticky` element in the body stick to the card instead of the page.
      overflow: 'clip',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      width: '100%',
      color: 'text/primary',
      '&:active': {
        backgroundColor: 'background/active',
      },
      '&:hover:not(:active)': {
        backgroundColor: 'background/hover',
      },
      _hover: {
        color: 'text/primary',
      },
      _open: {
        backgroundColor: 'background/secondary',
        // Figma draws this stroke inside the 48px header, so as a real border the open header
        // is 49px. Swap for `boxShadow: 'inset 0 -1px 0 {colors.border/minimal}'` if exact
        // 48px parity is ever needed.
        borderBottom: '1px solid',
        borderBottomColor: 'border/minimal',
      },
    },
    collapsible: {
      overflow: 'clip',
      _open: {
        animationName: 'expand-height, fade-in',
        animationDuration: 'moderate',
      },
      _closed: {
        animationName: 'collapse-height, fade-out',
        animationDuration: 'moderate',
      },
    },
    // Figma's footer content slot is 24px tall in both sizes; with Card's `sm` block
    // padding (8/8) that makes the 40px footer shown in the design.
    footer: {
      alignItems: 'center',
      display: 'flex',
      minHeight: '40',
    },
    // Flex, not the default block: an inline-block svg in a block box picks up the line box's
    // extra leading, which made an lg header with an icon 57.5px instead of Figma's 56px.
    icon: {
      alignItems: 'center',
      color: 'icon/secondary',
      display: 'flex',
    },
    subtitle: {
      color: 'text/secondary',
    },
    suffix: {
      color: 'text/secondary',
      mx: '16',
      textStyle: 'body/md/regular',
    },
    title: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      // Under `flex-start` a nowrap child keeps its full width, so cap it or `truncate` never engages.
      '& > *': {
        maxWidth: '100%',
      },
    },
  },
  variants: {
    noPadding: {
      false: {},
      true: {
        body: { padding: 0 },
      },
    },
    size: {
      md: {
        header: { textStyle: 'body/md/regular', paddingX: '16', paddingY: '12' },
        subtitle: { textStyle: 'body/sm/regular' },
      },
      lg: {
        header: { textStyle: 'body/lg/regular', padding: '16' },
        subtitle: { textStyle: 'body/md/regular' },
      },
    },
  },
  defaultVariants: {
    noPadding: false,
    size: 'lg',
  },
});

export default expandableCardSlotRecipe;
